// Full-page card detail — the phone's answer to the desktop board's modal.
// Tapping a board card pushes a full-screen page (over the bottom nav) with the
// item's spec, evidence, comments, and the actions the relay permits for that
// card type. Mirrors ../fabrika/web/src/views/board.ts's open*Detail handlers,
// trimmed to what a paired phone is allowed to do.
import { el, clear } from "../dom.js";
import { diffView } from "../diff.js";
import { STAGE_ORDER } from "../types.js";
import type { Attempt, BigTask, Comment, Decision, Evidence, Plan, ReviewItem, Task } from "../types.js";
import { taskDetailRows } from "./plan-task.js";

export type ApiFetch = <T>(method: string, path: string, body?: unknown) => Promise<T>;

export interface BoardCtx {
  api: ApiFetch;
  onChanged: () => void;
}

interface Page {
  body: HTMLElement;
  close: () => void;
}

// openDetailPage mounts a full-screen overlay with a back header and a
// scrollable body. It stacks above the bottom nav and closes on Back or Escape.
function openDetailPage(title: string, subtitle?: string): Page {
  const body = el("div", { class: "detail-page-body" });
  const close = () => {
    overlay.remove();
    document.removeEventListener("keydown", onKey);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  const back = el("button", { class: "detail-back", onclick: close }, [
    backIcon(),
    "Back",
  ]);
  const overlay = el("div", { class: "detail-page" }, [
    el("header", { class: "topbar detail-page-head" }, [back, el("h2", {}, [title])]),
    ...(subtitle ? [el("p", { class: "detail-page-sub" }, [subtitle])] : []),
    body,
  ]);
  document.addEventListener("keydown", onKey);
  document.body.append(overlay);
  body.scrollTo?.(0, 0);
  return { body, close };
}

function backIcon(): HTMLElement {
  const span = el("span", { class: "detail-back-icon" });
  span.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>';
  return span;
}

// act runs a mutating call, then closes the page and refreshes the board on
// success; failures surface inline at the top of the page instead.
async function act(page: Page, ctx: BoardCtx, errSlot: HTMLElement, fn: () => Promise<unknown>): Promise<void> {
  errSlot.replaceChildren();
  try {
    await fn();
    page.close();
    ctx.onChanged();
  } catch (e) {
    errSlot.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]));
  }
}

// ── Shared bits ──────────────────────────────────────────────────────────────

function fieldRow(label: string, value: (Node | string)[]): HTMLElement {
  return el("div", { class: "detail-field" }, [
    el("span", { class: "field-label" }, [label]),
    el("span", { class: "field-value" }, value),
  ]);
}

function section(heading: string, ...children: (Node | string)[]): HTMLElement {
  return el("div", { class: "detail-section" }, [
    el("div", { class: "section-title" }, [heading]),
    ...children,
  ]);
}

function actionRow(buttons: HTMLElement[]): HTMLElement {
  return el("div", { class: "actions" }, buttons);
}

function btn(label: string, cls: string, onclick: () => void): HTMLButtonElement {
  return el("button", { class: cls, onclick }, [label]) as HTMLButtonElement;
}

// stageRow renders one verification pill per gate stage, in run order — the
// same evidence the desktop board shows on a review card.
function stageRow(ev?: Evidence): HTMLElement {
  const stages = ev?.stages ?? {};
  const chips = STAGE_ORDER.filter((s) => stages[s]).map((s) => {
    const r = stages[s];
    const cls = r.skipped ? "stage skip" : r.pass ? "stage pass" : "stage fail";
    const mark = r.skipped ? "–" : r.pass ? "✓" : "✗";
    return el("span", { class: cls, title: r.output ? r.output.slice(-2000) : "" }, [`${mark} ${s}`]);
  });
  if (chips.length === 0) return el("div", { class: "stage-row" }, [el("span", { class: "meta" }, ["no gate stages"])]);
  return el("div", { class: "stage-row" }, chips);
}

// failureBlock surfaces why a run went red: the first failing stage's output,
// plus the agent's own log (which carries crashes before any gate ran).
function failureBlock(attempt: Attempt): HTMLElement {
  const stages = attempt.evidence?.stages ?? {};
  const failed = STAGE_ORDER.find((s) => stages[s] && !stages[s].skipped && !stages[s].pass);
  const children: HTMLElement[] = [];
  let stageOut = "";
  if (failed) {
    const r = stages[failed];
    stageOut = (r.output ?? "").trim();
    children.push(el("p", { class: "fail-head" }, [`✗ ${failed} failed (exit ${r.exitCode})`]));
    children.push(
      stageOut
        ? el("pre", { class: "fail-out" }, [r.output.slice(-8000)])
        : el("p", { class: "meta" }, [`(command exited ${r.exitCode} with no output — check the agent log)`]),
    );
  }
  const log = (attempt.log ?? "").trim();
  if (log) {
    children.push(el("details", { class: "log-wrap", open: !stageOut }, [
      el("summary", {}, ["Agent log"]),
      el("pre", { class: "fail-out" }, [log.slice(-8000)]),
    ]));
  }
  if (children.length === 0) return el("span", {});
  return el("div", { class: "failure" }, children);
}

function evidenceBlock(attempt: Attempt | null | undefined): (Node | string)[] {
  if (!attempt) return [];
  const out: (Node | string)[] = [stageRow(attempt.evidence)];
  if (attempt.result && attempt.result !== "pass") out.push(failureBlock(attempt));
  const diff = attempt.evidence?.diff?.trim();
  out.push(diff ? diffView(diff) : el("p", { class: "meta" }, ["(no diff produced)"]));
  return out;
}

// evidenceSection wraps the gate evidence under a heading so every detail page
// that has an attempt presents it the same way; nothing renders without one.
function evidenceSection(attempt: Attempt | null | undefined): Node | string {
  if (!attempt) return "";
  return section("Evidence", ...evidenceBlock(attempt));
}

// commentsBlock: the task's conversation thread plus a composer (POST comments
// is on the relay allowlist). Lazy-loads on mount.
function commentsBlock(taskId: string, ctx: BoardCtx): HTMLElement {
  const listSlot = el("div", { class: "comment-list" }, [el("p", { class: "meta" }, ["Loading comments…"])]);
  const input = el("textarea", { placeholder: "Add a comment…", rows: "2" }) as HTMLTextAreaElement;
  const err = el("div", {});

  const reload = async () => {
    try {
      const comments = await ctx.api<Comment[]>("GET", `/api/tasks/${taskId}/comments`);
      clear(listSlot);
      if (comments.length === 0) {
        listSlot.append(el("p", { class: "meta" }, ["No comments yet."]));
        return;
      }
      for (const c of comments) {
        listSlot.append(el("div", { class: "comment" }, [
          el("span", { class: "comment-author" }, [c.authorType === "user" ? "You" : c.authorId || c.authorType]),
          c.body ? el("p", { class: "comment-body" }, [c.body]) : "",
        ]));
      }
    } catch {
      clear(listSlot);
      listSlot.append(el("p", { class: "error" }, ["Failed to load comments."]));
    }
  };

  const submit = btn("Comment", "primary", async () => {
    const text = input.value.trim();
    if (!text) return;
    err.replaceChildren();
    submit.disabled = true;
    try {
      await ctx.api("POST", `/api/tasks/${taskId}/comments`, { body: text, attachments: [] });
      input.value = "";
      await reload();
    } catch (e) {
      err.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]));
    } finally {
      submit.disabled = false;
    }
  });

  void reload();
  return section("Comments", listSlot, el("div", { class: "field" }, [input]), err, actionRow([submit]));
}

// promptThen reads a one-line reason via the native prompt, then runs `fn` with
// it when non-empty — used by Request changes / Kick back, which require text.
function promptThen(message: string, fn: (text: string) => void): void {
  const text = window.prompt(message)?.trim();
  if (text) fn(text);
}

// ── Per-type detail pages ─────────────────────────────────────────────────────

export function openPlanDetail(p: Plan, ctx: BoardCtx): void {
  const page = openDetailPage(p.bigTask?.title ?? "Plan", "Proposed plan — approve to start the work.");
  const err = el("div", {});
  const tasks = p.tasks ?? [];
  page.body.append(
    err,
    p.bigTask?.intent ? el("p", { class: "detail-spec" }, [p.bigTask.intent]) : "",
    section("Details",
      fieldRow("Status", [p.status]),
      fieldRow("Tasks", [String(tasks.length)]),
    ),
    section("Tasks", ...tasks.map((t) =>
      el("div", { class: "card" }, [
        el("h3", {}, [t.title]),
        ...taskDetailRows(t).map((r) => fieldRow(r.label, [r.value])),
      ]),
    )),
    actionRow([
      btn("Approve plan", "primary", () => act(page, ctx, err, () => ctx.api("POST", `/api/plans/${p.id}/approve`))),
      btn("Request changes", "", () => promptThen("What should the planner change?", (feedback) =>
        act(page, ctx, err, () => ctx.api("POST", `/api/plans/${p.id}/revise`, { feedback })))),
      btn("Reject", "danger", () => act(page, ctx, err, () => ctx.api("POST", `/api/plans/${p.id}/reject`))),
    ]),
  );
}

export function openDecideDetail(d: Decision, ctx: BoardCtx): void {
  const page = openDetailPage(d.question, "An open question — answer it to unblock the plan.");
  const err = el("div", {});
  const promote = el("input", { type: "checkbox" }) as HTMLInputElement;
  const free = el("input", { type: "text", placeholder: "Or type an answer…" }) as HTMLInputElement;
  const answer = (text: string) => {
    const a = text.trim();
    if (!a) return;
    act(page, ctx, err, () => ctx.api("POST", `/api/decisions/${d.id}/answer`, { answer: a, promote: promote.checked }));
  };
  page.body.append(
    err,
    d.context ? el("p", { class: "detail-spec" }, [d.context]) : "",
    (d.options ?? []).length
      ? section("Options", actionRow((d.options ?? []).map((o) => btn(o, "", () => answer(o)))))
      : "",
    section("Answer",
      el("div", { class: "field" }, [free]),
      el("label", { class: "meta" }, [promote, "Save as a convention (steer future runs)"]),
      actionRow([btn("Submit answer", "primary", () => answer(free.value))]),
    ),
  );
}

export function openReviewDetail(it: ReviewItem, ctx: BoardCtx): void {
  const { task, attempt } = it;
  const page = openDetailPage(task.title, "Finished — review the evidence, then merge or send it back.");
  const err = el("div", {});
  const green = task.status === "review";
  page.body.append(
    err,
    taskFields(task),
    evidenceSection(attempt),
    actionRow([
      green
        ? btn("Merge", "primary", () => act(page, ctx, err, () => ctx.api("POST", `/api/tasks/${task.id}/accept`)))
        : btn("Retry", "primary", () => act(page, ctx, err, () => ctx.api("POST", `/api/tasks/${task.id}/retry`))),
      green
        ? btn("Request changes", "", () => promptThen("Describe the changes you want.", (g) =>
            act(page, ctx, err, () => ctx.api("POST", `/api/tasks/${task.id}/request-changes`, { guidance: g }))))
        : "",
      btn("Kick back", "danger", () => promptThen("A reason is required to kick a task back.", (reason) =>
        act(page, ctx, err, () => ctx.api("POST", `/api/tasks/${task.id}/reject`, { reason })))),
    ].filter(Boolean) as HTMLElement[]),
    commentsBlock(task.id, ctx),
  );
}

export function openAuditDetail(it: ReviewItem, ctx: BoardCtx): void {
  const { task, attempt } = it;
  const page = openDetailPage(task.title, "Auto-merged — confirm it looks right or revert.");
  const err = el("div", {});
  page.body.append(
    err,
    taskFields(task),
    evidenceSection(attempt),
    actionRow([
      btn("Looks good", "primary", () => act(page, ctx, err, () => ctx.api("POST", `/api/tasks/${task.id}/audit-ok`))),
      btn("Revert", "danger", () => act(page, ctx, err, () => ctx.api("POST", `/api/tasks/${task.id}/revert`))),
    ]),
    commentsBlock(task.id, ctx),
  );
}

// Subtitles keep the task pages reading like the gate pages — a one-line "where
// this is in the lifecycle" under the title.
const TASK_SUBTITLE: Record<string, string> = {
  ready: "Queued — waiting for an agent to pick it up.",
  claimed: "Claimed — an agent is starting on it.",
  running: "In progress — an agent is working on it now.",
  verifying: "Running its verification gates.",
  merged: "Merged into the base branch.",
  closed: "Closed.",
};

export function openTaskDetail(t: Task, ctx: BoardCtx): void {
  const page = openDetailPage(t.title, TASK_SUBTITLE[t.status]);
  const err = el("div", {});
  page.body.append(
    err,
    t.spec ? el("p", { class: "detail-spec" }, [t.spec]) : "",
    taskFields(t),
  );
  // Pull the latest attempt's evidence (stages + diff) lazily — the list view
  // doesn't carry it. Rendered under an Evidence heading, matching the gate pages.
  const evSlot = el("div", {});
  page.body.append(evSlot);
  ctx.api<{ task: Task; attempts: Attempt[] }>("GET", `/api/tasks/${t.id}`)
    .then(({ attempts }) => {
      const a = attempts && attempts.length ? attempts[attempts.length - 1] : null;
      const node = evidenceSection(a);
      if (node !== "") evSlot.append(node);
    })
    .catch(() => {});
  if (t.status === "closed") {
    page.body.append(actionRow([
      btn("Retry", "primary", () => act(page, ctx, err, () => ctx.api("POST", `/api/tasks/${t.id}/retry`))),
    ]));
  }
  page.body.append(commentsBlock(t.id, ctx));
}

const BIGTASK_SUBTITLE: Record<string, string> = {
  backlog: "Parked in the backlog — plan it to start, or delete it.",
  draft: "Queued for planning.",
  planning: "Being decomposed — it'll land in Approve when ready.",
  error: "Planning failed.",
};

export function openBigTaskDetail(b: BigTask, ctx: BoardCtx): void {
  const page = openDetailPage(b.title, BIGTASK_SUBTITLE[b.status]);
  const err = el("div", {});
  page.body.append(
    err,
    b.intent ? el("p", { class: "detail-spec" }, [b.intent]) : "",
    section("Details", fieldRow("Status", [b.status])),
    b.error ? el("p", { class: "fail-head" }, [b.error]) : "",
  );
  if (b.status === "backlog") {
    page.body.append(actionRow([
      btn("Plan it", "primary", () => act(page, ctx, err, () => ctx.api("POST", `/api/bigtasks/${b.id}/plan`))),
      btn("Delete", "danger", () => {
        if (!confirm(`Delete "${b.title}"?`)) return;
        act(page, ctx, err, () => ctx.api("DELETE", `/api/bigtasks/${b.id}`));
      }),
    ]));
  }
  if (b.status === "error") {
    page.body.append(actionRow([
      btn("Retry planning", "primary", () => act(page, ctx, err, () => ctx.api("POST", `/api/bigtasks/${b.id}/plan`))),
      btn("Delete", "danger", () => {
        if (!confirm(`Delete "${b.title}"?`)) return;
        act(page, ctx, err, () => ctx.api("DELETE", `/api/bigtasks/${b.id}`));
      }),
    ]));
  }
}

function taskFields(t: Task): HTMLElement {
  const rows: HTMLElement[] = [fieldRow("Status", [t.status])];
  if (t.riskTier) rows.push(fieldRow("Risk", [t.riskTier]));
  if (t.agentId) rows.push(fieldRow("Agent", [t.agentId]));
  if (t.branch) rows.push(fieldRow("Branch", [el("code", {}, [t.branch])]));
  return section("Details", ...rows);
}
